import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useUser } from '@/queries/useUser';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import { createSubscription } from '@/lib/api/subscriptions';
import { getCustomerId } from '@/lib/api/stripe';
import { stanNotify } from '@/lib/stanNotify';
import { getUUID } from '@/lib/helpers';
import { hasActiveSubscription } from '../logic/subscriptionStatus.logic';
import { useDuplicatePaymentCheck } from './useDuplicatePaymentCheck';
import { ALL_SUBSCRIPTION_STORAGE_KEYS, SUBSCRIPTION_STORAGE_KEYS } from '../constants';
import {
    fireNorthbeamPurchase,
    fireNorthbeamReactivation,
    trackPaymentMethodCreated,
    trackSubscriptionAttempted,
    trackSubscriptionCreating,
    trackSubscriptionFailed,
    trackSubscriptionSuccess,
    trackTermsAgreed,
} from '../logic/subscriptionAnalytics.logic';
import type { StripePaymentInputRef } from '@/components/third-party/StripePaymentInput/StripePaymentInput';
import type { StripeElements } from '@stripe/stripe-js';

export interface SubscriptionState {
    priceId: string | null;
    trialDays: number;
    creditCardRequired: boolean;
    promoId: string | null;
}

/**
 * Hook for handling subscription creation on the payment page
 */
export const useSubscriptionCreation = () => {
    const { push } = useStanRouter();
    const { user, refetchUser } = useUser();
    const { currency, creatorMonthlyPrice, creatorAnnualPrice, creatorAnnualPriceId } = useSubscriptionPrices();

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [subscriptionState, setSubscriptionState] = useState<SubscriptionState>({
        priceId: null,
        trialDays: 14,
        creditCardRequired: true,
        promoId: null,
    });

    const customerIdRef = useRef<string | null>(null);
    const idempotencyKeyRef = useRef<string>(getUUID());
    const customerIdPromiseRef = useRef<Promise<string> | null>(null);
    const hasTrackedAttemptRef = useRef(false);

    const hasPastSubscription = user?.has_subscription_history ?? false;
    const hasActiveSubscriptionStatus = hasActiveSubscription(user);

    // Memoize userMeta to prevent infinite loops in useEffect dependencies
    const userMeta = useMemo(
        () => ({
            user_id: user?.user_id,
            username: user?.username,
            email: user?.email,
            full_name: user?.full_name,
        }),
        [user?.user_id, user?.username, user?.email, user?.full_name]
    );

    // Get plan info for analytics
    const getPlanInfo = useCallback(() => {
        const isYearly = subscriptionState.priceId === creatorAnnualPriceId;
        return {
            price: isYearly ? creatorAnnualPrice : creatorMonthlyPrice,
            currency,
            planName: isYearly ? 'Creator Annual' : 'Creator Monthly',
            isYearly,
        };
    }, [subscriptionState.priceId, creatorAnnualPriceId, creatorAnnualPrice, creatorMonthlyPrice, currency]);

    const preventBackButton = (url: string) => {
        for (let i = 0; i < 10; i++) window.history.pushState({}, String(i), url);
    };

    const clearSubscriptionData = () => {
        ALL_SUBSCRIPTION_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
        [null, 'stanwith.me', 'stan.store'].forEach(domain => {
            document.cookie = `page=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${domain ? ` domain=${domain};` : ''}`;
        });
    };

    const createSubscriptionWithPaymentMethod = useCallback(
        async (paymentMethodId?: string) => {
            if (!customerIdRef.current || !subscriptionState.priceId) {
                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: 'Please reach out to us at friends@stanwith.me.',
                });
                setIsSubmitting(false);
                return;
            }

            trackSubscriptionCreating(userMeta);

            const response = await createSubscription({
                customerId: customerIdRef.current,
                priceId: subscriptionState.priceId,
                trialDays: subscriptionState.trialDays,
                idempotencyKey: idempotencyKeyRef.current,
                ...(paymentMethodId && { paymentMethodId }),
                ...(subscriptionState.promoId && { promoId: subscriptionState.promoId }),
            });

            if (response.status === 'active' || response.status === 'trialing') {
                if (hasPastSubscription) fireNorthbeamReactivation();
                await refetchUser();

                const planInfo = getPlanInfo();
                trackSubscriptionSuccess(userMeta, planInfo);

                if (response.id && response.latest_invoice?.id) {
                    fireNorthbeamPurchase(response.id, response.latest_invoice.id, planInfo, user?.user_id);
                }

                clearSubscriptionData();

                // Set a flag to indicate subscription was just created
                // This prevents UserProvider from redirecting back to subscribe page
                // while the user data is being updated
                sessionStorage.setItem('subscription_just_created', 'true');
                preventBackButton('/dashboard');
                push('/dashboard');
            } else {
                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: 'Please reach out to us at friends@stanwith.me.',
                });
                setIsSubmitting(false);
            }
        },
        [subscriptionState, user, hasPastSubscription, getPlanInfo, refetchUser, push, userMeta]
    );

    // Duplicate payment method check hook
    const duplicatePaymentCheck = useDuplicatePaymentCheck({
        userMeta,
        hasPastSubscription,
        onConfirm: createSubscriptionWithPaymentMethod,
    });

    // Read localStorage on mount
    useEffect(() => {
        const priceId = localStorage.getItem(SUBSCRIPTION_STORAGE_KEYS.PRICE_ID);
        if (!priceId) {
            push('/subscribe/plan');
            return;
        }

        const trialDaysStr = localStorage.getItem(SUBSCRIPTION_STORAGE_KEYS.TRIAL_DAYS);
        const trialDays = trialDaysStr ? parseInt(trialDaysStr, 10) : 14;

        const storedPromoId = localStorage.getItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);

        setSubscriptionState({
            priceId,
            trialDays: Number.isInteger(trialDays) && trialDays >= 0 ? trialDays : 14,
            creditCardRequired:
                localStorage.getItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED)?.toLowerCase() !== 'false',
            promoId: storedPromoId !== 'undefined' ? storedPromoId : null,
        });

        setIsLoading(false);
        customerIdPromiseRef.current = getCustomerId();
    }, []);

    // Track subscription attempted once when user data is available
    useEffect(() => {
        if (userMeta.user_id && !hasTrackedAttemptRef.current) {
            hasTrackedAttemptRef.current = true;
            trackSubscriptionAttempted(userMeta);
        }
    }, [userMeta]);

    const handleSubmit = useCallback(
        async (stripeRef: React.RefObject<StripePaymentInputRef | null>) => {
            if (!termsAgreed) return;

            setIsSubmitting(true);
            trackTermsAgreed(userMeta);

            try {
                if (!customerIdRef.current) {
                    customerIdRef.current = await (customerIdPromiseRef.current || getCustomerId());
                }

                let paymentMethodId: string | undefined;

                if (subscriptionState.creditCardRequired && stripeRef.current) {
                    paymentMethodId =
                        (await stripeRef.current.createPaymentMethod(
                            customerIdRef.current,
                            user?.full_name || '',
                            user?.email || ''
                        )) || undefined;

                    if (!paymentMethodId) {
                        setIsSubmitting(false);
                        return;
                    }
                    trackPaymentMethodCreated(userMeta, getPlanInfo());

                    // Check for duplicate payment method (only for new sign ups)
                    const hasDuplicates = await duplicatePaymentCheck.checkForDuplicates(paymentMethodId);
                    if (hasDuplicates) {
                        // Modal is shown, wait for user action
                        return;
                    }
                }

                await createSubscriptionWithPaymentMethod(paymentMethodId);
            } catch (error: unknown) {
                const errorMessage =
                    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    'Please try again later.';
                stanNotify({ type: 'error', title: "Couldn't create a subscription", text: errorMessage });
                trackSubscriptionFailed(userMeta, errorMessage);
                setIsSubmitting(false);
            }
        },
        [
            termsAgreed,
            subscriptionState,
            user,
            getPlanInfo,
            userMeta,
            createSubscriptionWithPaymentMethod,
            duplicatePaymentCheck,
        ]
    );

    const handleExpressCheckout = useCallback(
        async (
            elements: StripeElements,
            confirmExpressCheckout: (elements: StripeElements, customerId: string) => Promise<string | null>
        ) => {
            setIsSubmitting(true);

            try {
                if (!customerIdRef.current) {
                    customerIdRef.current = await (customerIdPromiseRef.current || getCustomerId());
                }

                const paymentMethodId = await confirmExpressCheckout(elements, customerIdRef.current);

                if (!paymentMethodId) {
                    setIsSubmitting(false);
                    return;
                }

                trackPaymentMethodCreated(userMeta, getPlanInfo());

                // Check for duplicate payment method (only for new sign ups)
                const hasDuplicates = await duplicatePaymentCheck.checkForDuplicates(paymentMethodId);
                if (hasDuplicates) {
                    // Modal is shown, wait for user action
                    return;
                }

                await createSubscriptionWithPaymentMethod(paymentMethodId);
            } catch (error: unknown) {
                const errorMessage =
                    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    'Please try again later.';
                stanNotify({ type: 'error', title: "Couldn't create a subscription", text: errorMessage });
                trackSubscriptionFailed(userMeta, errorMessage);
                setIsSubmitting(false);
            }
        },
        [userMeta, getPlanInfo, createSubscriptionWithPaymentMethod, duplicatePaymentCheck]
    );

    return {
        isLoading,
        isSubmitting,
        termsAgreed,
        setTermsAgreed,
        subscriptionState,
        hasPastSubscription,
        hasActiveSubscriptionStatus,
        handleSubmit,
        handleExpressCheckout,
        // Duplicate payment modal
        duplicatePaymentCheck,
    };
};
